import { Box, InputAdornment, styled, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../features/authSlice";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { getExportNewsLetter, getNewsLetter } from "../../api/staticPageApi";
import theme from "../../theme";
import { Search } from "@mui/icons-material";
import CommonButton from "../../common/components/CommonButton";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import moment from "moment";

const Cell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    padding: 5,
    textAlign: 'center',
    fontSize: 13,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
    background: theme.palette.secondary.main,
    color: theme.palette.primary.main
}));

const InnerCell = styled(TableCell)(({ theme }) => ({
    padding: 10,
    textAlign: 'center',
    fontSize: 14,
    color: theme.palette.background.dark,
    borderRight: `1px solid ${theme.palette.background.secondary}`,
}));

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 1,
    height: 'auto', // Let it adjust based on content
    maxHeight: '400px', // Fixed maximum height
    overflow: 'auto', // Enable scrolling
}));

const Newsletter = () => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state) => state.auth);
    const [searchText, setSearchText] = useState('');
    const [newsLetters, setNewsLetters] = useState(null);
    const [pageNo, setPageNo] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [clearSearch, setClearSearch] = useState(false);

    const getNewsLetterData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getNewsLetter(pageNo + 1, pageSize, searchText);
            setNewsLetters(response.data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        getNewsLetterData();
    }, [pageNo, pageSize]);

    const downloadCSV = (data, filename = 'data.csv') => {
        const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleChangePage = (event, newPage) => {
        setPageNo(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setPageSize(parseInt(event.target.value, 10));
        setPageNo(0);
    };

    const resetFiltersAndFetch = () => {
        const fetchWithEmptyFilters = async () => {
            try {
                dispatch(setLoading({ loading: true }));
                const response = await getNewsLetter(pageNo + 1, pageSize, '');
                setNewsLetters(response.data);
                dispatch(setLoading({
                    loading: false
                }));
            } catch (err) {
                setNewsLetters(null);
                const errorMessage = err.response?.data?.errors?.[0]?.message ||
                    err.response?.data?.message ||
                    err?.response?.data?.error;
                showNotification(errorMessage, 'error');
                dispatch(setLoading({ loading: false }));
            }
        };
        fetchWithEmptyFilters();
    };

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <Box display="flex" justifyContent="space-between">
                <Typography fontSize={20} fontWeight={600}>Subscribers</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                    <TextField
                        placeholder="Search By Email"
                        variant="outlined"
                        size="small"
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                background: theme.palette.background.main,
                                '&:hover fieldset': {
                                    border: '1px solid lightgray',
                                },
                                '&.Mui-focused fieldset': {
                                    border: '1px solid lightgray'
                                },
                            },
                            '& input': {
                                height: '25px',
                                fontSize: '16px',
                                fontWeight: 500,
                                width: '130px'
                            }
                        }}
                        onChange={(e) => {
                            setSearchText(e.target.value);
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ fontSize: 30, color: theme.palette.background.dark }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <CommonButton
                        label={clearSearch ? "Clear" : "Search"}
                        marginBottom="0px"
                        width="120px"
                        fontWeight={600}
                        value={searchText}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={() => {
                            if (clearSearch) {
                                setSearchText('');
                                resetFiltersAndFetch();
                            } else {
                                getNewsLetterData();
                            }
                            setClearSearch(!clearSearch);
                            setPageNo(0);
                            setNewsLetters(null);
                        }}
                    />
                    <CommonButton
                        label="Export To Csv"
                        marginBottom="0px"
                        width="140px"
                        fontWeight={600}
                        background={theme.palette.secondary.main}
                        color="White"
                        handleClick={async () => {
                            try {
                                dispatch(setLoading({
                                    loading: true
                                }));
                                const response = await getExportNewsLetter();
                                downloadCSV(response, 'subscribers.csv');
                                dispatch(setLoading({
                                    loading: false
                                }));
                            } catch (err) {
                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                showNotification(errorMessage, 'error');
                                dispatch(setLoading({
                                    loading: false
                                }));
                            }
                        }}
                    />
                </Box>
            </Box>
            <Box pt={1} pb={0}>
                <StyledTableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: theme.palette.grey[100] }}>
                                <Cell width={'60%'}>Email</Cell>
                                <Cell>Subscribe Date</Cell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {newsLetters?.data.length > 0 ? newsLetters?.data?.map((newsletter) => (
                                <TableRow key={newsletter.subscriber_id}>
                                    <InnerCell style={{ color: 'black' }}>
                                        {newsletter.email}
                                    </InnerCell>
                                    <InnerCell>
                                        {moment(newsletter.subscribed_at).format('MMM DD, YYYY')}
                                    </InnerCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={2} align="center">
                                        <Typography>No Subscribers Found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
            </Box>

            {/* Pagination Component */}
            <Box sx={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                    component="div"
                    count={newsLetters?.pagination?.total}
                    rowsPerPage={pageSize}
                    page={pageNo}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    sx={{
                        '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem',
                        },
                    }}
                />
            </Box>
        </Fragment>
    )
}

export default Newsletter;